// src/contexts/CreatorContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { TokenContext } from "./TokenContext";
import { requestsClient } from "../utils/RequestsClients";
import { getTemplateOverride } from "../utils/templateOverride";

export const CreatorContext = createContext({
	creator: null,
	setCreator: () => {},
	loading: true,
});

const determineTextColor = (backgroundColor, opacity) => {
	// Convertimos el color hexadecimal a RGB para obtener los componentes de color
	const r = parseInt(backgroundColor.slice(1, 3), 16);
	const g = parseInt(backgroundColor.slice(3, 5), 16);
	const b = parseInt(backgroundColor.slice(5, 7), 16);

	// Usamos la fórmula de luminosidad relativa para determinar si el color de texto debe ser blanco o negro
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Devolvemos blanco si el color de fondo es oscuro, negro si es claro
	return luminance > 0.55
		? `rgba(0, 0, 0, ${opacity})`
		: `rgba(255, 255, 255, ${opacity})`;
};

export const CreatorProvider = ({ children }) => {
	const [creator, setCreator] = useState(null);
	const [styleCreated, setStyleCreated] = useState(false);
	const [loading, setLoading] = useState(true);
	const { appId, pathId, creator: iframeCreator } = useContext(TokenContext);

	const getWebTemplate = (data) => {
		const templateParam = getTemplateOverride();

		if (templateParam) {
			// "gaps" es una template restringida: solo se muestra si el módulo está activo.
			// No se permite forzar por ?template=gaps.
			if (templateParam === "gaps") {
				// Ignorar override: mantener la template asignada por el negocio
			}
			// Soporte para templates de shop: shop1, shop2, shop3, shop4, shop5, drugstore
			else if (/^(shop[1-6]|drugstore)$/.test(templateParam)) {
				data.webTemplate = templateParam;
			}
			// Soporte para nombre completo de template: restaurant19, burgerbar, trainer1, etc.
			else if (/^(restaurant\d+|burgerbar|grillhouse|riquino|flora|rovica|parcelona|balmar|trainer\d+|barber\d+|barberiamarco\d*|hairdresser\d+|muse|vianca|essenza)$/.test(templateParam)) {
				data.webTemplate = templateParam;
			}
			// Soporte para templates de restaurante por número: 1-20
			else if (/^\d{1,2}$/.test(templateParam) && Number(templateParam) >= 1 && Number(templateParam) <= 20) {
				data.webTemplate = `restaurant${templateParam}`;
			}
		}

		return data;
	};

	useEffect(() => {
		const getCreator = async () => {
			try {
				if (iframeCreator) {
					// Aplicar también el override de template desde URL cuando viene de iframe
					const creatorWithTemplate = getWebTemplate({ ...iframeCreator });
					console.log("[DEBUG moduleFlags] iframeCreator.app:", JSON.stringify(creatorWithTemplate?.app, null, 2));
					console.log("[DEBUG moduleFlags] iframeCreator.app.modules:", creatorWithTemplate?.app?.modules);
					setCreator(creatorWithTemplate);
				} else {
					const res = await requestsClient.get(
						"creators",
						{
							params: {
								filter: JSON.stringify({ appId: appId }),
							},
						},
						undefined,
						pathId
					);
					res.data = getWebTemplate(res.data);
					console.log("[DEBUG moduleFlags] creator.app:", JSON.stringify(res.data?.app, null, 2));
					console.log("[DEBUG moduleFlags] creator.app.modules:", res.data?.app?.modules);
					setCreator(res.data);
				}
			} catch (error) {
				console.error("Error al cargar creator", error);
			}
			setLoading(false);
		};
		getCreator();
	}, [appId, pathId, iframeCreator]);

	useEffect(() => {
		if (creator && !styleCreated) {
			setStyleCreated(true);
			const style = document.createElement("style");
			// Add the ::selection CSS rule
			style.textContent = `
				::selection {
					background:  ${creator.color ?? "#bdc3c7"};
					color: ${creator.color ? determineTextColor(creator.color, 1) : "inherit"};
				}
				/* Firefox uses a different pseudo-element name */
				::-moz-selection {
					background: ${creator.color ?? "#bdc3c7"};
					color: ${creator.color ? determineTextColor(creator.color, 1) : "inherit"};
				}
				`;

			// Append to the <head>
			document.head.appendChild(style);
		}
	}, [creator, styleCreated]);

	return (
		<CreatorContext.Provider value={{ creator, setCreator, loading }}>
			{children}
		</CreatorContext.Provider>
	);
};
